import { columns } from '@/components/files/columns';
import { FileGridItemActions } from '@/components/files/file-grid-item-actions';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/components/ui/data-table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { FileData } from '@/types/file';
import { Check, Grid, Link, List, UploadCloud } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import supabase from '../api/supabase';

// ============= Constants and Types =============

// Default configuration for file uploads
const DEFAULT_CONFIG = {
  allowedFileTypes: ['image/jpeg', 'image/png', 'image/svg+xml'],
  maxFileSize: 5 * 1024 * 1024, // 5MB
};

// Available bucket folders
const BUCKET_FOLDERS = {
  AVATAR: 'Avatar',
  IMAGE: 'Image',
} as const;

type BucketFolder = (typeof BUCKET_FOLDERS)[keyof typeof BUCKET_FOLDERS];
type ViewMode = 'grid' | 'list';

// ============= Main Component =============

const FileManager = () => {
  // ============= State Management =============
  const [files, setFiles] = useState<FileData[]>([]);
  const [currentFolder, setCurrentFolder] = useState<BucketFolder>(BUCKET_FOLDERS.AVATAR);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [copyStatus, setCopyStatus] = useState<{ [key: string]: boolean }>({});

  // ============= File Operations =============

  // Fetch files from current folder
  const fetchFiles = useCallback(async () => {
    try {
      const { data, error } = await supabase.storage.from('Files').list(currentFolder);

      if (error) throw error;

      const filesWithUrls = await Promise.all(
        data.map(async file => {
          const fileUrl = supabase.storage
            .from('Files')
            .getPublicUrl(`${currentFolder}/${file.name}`);

          return {
            ...file,
            url: fileUrl.data.publicUrl,
            id: `${currentFolder}-${file.name}`,
            bucket_id: 'Files',
            owner: 'current-user',
            status: Math.random() > 0.8 ? 'Failed' : Math.random() > 0.3 ? 'Success' : 'Processing',
            email: `user${Math.floor(Math.random() * 100)}@example.com`,
            amount: Math.floor(Math.random() * 1000),
            uploadTime: new Date().toISOString(),
            size: file.metadata?.size || 0,
            metadata: {
              size: file.metadata?.size || 0,
              mimetype: file.metadata?.mimetype || 'application/octet-stream',
              ...file.metadata,
            },
          } as FileData;
        })
      );

      setFiles(filesWithUrls);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Failed to fetch files');
    }
  }, [currentFolder]);

  // Initial file fetch
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Handle file upload
  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file type and size
      if (!DEFAULT_CONFIG.allowedFileTypes.includes(file.type)) {
        toast.error('Invalid file type. Please upload only images.');
        return;
      }

      if (file.size > DEFAULT_CONFIG.maxFileSize) {
        toast.error(`File size must be less than ${DEFAULT_CONFIG.maxFileSize / 1024 / 1024}MB`);
        return;
      }

      const fileName = `${Date.now()}-${file.name}`;
      const { error } = await supabase.storage
        .from('Files')
        .upload(`${currentFolder}/${fileName}`, file);

      if (error) throw error;

      toast.success('File uploaded successfully');
      fetchFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    }
  };

  // Handle file deletion
  const handleDelete = async (file: FileData) => {
    try {
      const { error } = await supabase.storage
        .from('Files')
        .remove([`${currentFolder}/${file.name}`]);

      if (error) throw error;

      toast.success('File deleted successfully');
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  // Handle URL copy
  const handleCopyUrl = async (file: FileData) => {
    try {
      const success = await copyToClipboard(file.url);
      if (success) {
        setCopyStatus({ ...copyStatus, [file.id]: true });
        toast.success('URL copied to clipboard');

        // Reset copy status after 4 seconds
        setTimeout(() => {
          setCopyStatus(prev => ({ ...prev, [file.id]: false }));
        }, 4000);
      } else {
        throw new Error('Copy operation failed');
      }
    } catch (error) {
      console.error('Error copying URL:', error);
      toast.error('Failed to copy URL');
    }
  };

  // ============= Render Component =============
  return (
    <div className="flex flex-col sm:flex-row h-screen bg-white rounded-lg shadow-sm">
      {/* Sidebar Navigation */}
      <div className="flex-none w-[250px] min-w-[250px] border-b sm:border-b-0 sm:border-r p-4 space-y-4">
        <div className="space-y-2">
          <h1 className="text-2xl font-bold">File Manager</h1>
          <p className="text-sm text-muted-foreground">Manage your files and media</p>
        </div>

        <div className="flex flex-row gap-2 sm:flex-col sm:space-y-2">
          <Button
            variant={currentFolder === BUCKET_FOLDERS.AVATAR ? 'default' : 'outline'}
            onClick={() => setCurrentFolder(BUCKET_FOLDERS.AVATAR)}
            className="flex-1 justify-center"
          >
            Avatars
          </Button>
          <Button
            variant={currentFolder === BUCKET_FOLDERS.IMAGE ? 'default' : 'outline'}
            onClick={() => setCurrentFolder(BUCKET_FOLDERS.IMAGE)}
            className="flex-1 justify-center"
          >
            Images
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 w-full overflow-hidden">
        <div className="space-y-4 w-full">
          <Card className="border-none w-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 px-4">
              <div>
                <CardTitle>
                  {currentFolder === BUCKET_FOLDERS.AVATAR ? 'Avatars' : 'Images'}
                </CardTitle>
                <CardDescription>Manage your {currentFolder.toLowerCase()} files</CardDescription>
              </div>
              <div className="flex items-center space-x-2">
                {/* View Mode Toggle */}
                <div className="border rounded-md p-1 flex space-x-1">
                  <Button
                    variant={viewMode === 'grid' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('grid')}
                    className="h-8 w-8 p-0"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className="h-8 w-8 p-0"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                </div>
                {/* File Upload */}
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleUpload}
                  accept={DEFAULT_CONFIG.allowedFileTypes.join(',')}
                />
                <Button asChild>
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <UploadCloud className="h-4 w-4 mr-2" />
                    Upload
                  </label>
                </Button>
              </div>
            </CardHeader>

            <CardContent className="px-1 sm:p-4">
              {viewMode === 'grid' ? (
                <ScrollArea className="h-[calc(100vh-16rem)]">
                  <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {files.map(file => (
                      <div
                        key={file.id}
                        className="group relative bg-background border rounded-lg p-4 hover:shadow-lg transition-all"
                      >
                        <div className="absolute top-2 right-2 opacity-100  group-hover:opacity-100 transition-opacity z-10 flex gap-2">
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleCopyUrl(file)}
                          >
                            {copyStatus[file.id] ? (
                              <Check className="h-4 w-4 text-green-500" />
                            ) : (
                              <Link className="h-4 w-4" />
                            )}
                          </Button>
                          <FileGridItemActions file={file} onDelete={handleDelete} />
                        </div>
                        <div className="aspect-square rounded-md overflow-hidden bg-muted flex items-center justify-center">
                          <img
                            src={file.url}
                            alt={file.name}
                            className="w-full h-full object-contain"
                            style={{ objectFit: 'contain' }}
                            onError={e => {
                              const imgElement = e.target as HTMLImageElement;
                              imgElement.src = '/placeholder-image.png';
                              imgElement.classList.add('object-contain');
                            }}
                          />
                        </div>
                        <div className="mt-2">
                          <p className="text-sm font-medium truncate">{file.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {((file.metadata?.size ?? file.size) / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="w-full">
                  <div className=" overflow-x-auto p-1">
                    <DataTable
                      columns={columns({
                        onDelete: handleDelete,
                      })}
                      data={files}
                      searchKey="name"
                      isLoading={false}
                    />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default FileManager;
